/**
 * English message catalog — the source of truth for i18n keys.
 *
 * Keys are dot-namespaced by view/feature. `ko.ts` is typed against these keys
 * (Record<MessageKey, string>), so a missing or misspelled Korean key is a
 * compile error. Add new UI strings here first, then translate in ko.ts.
 *
 * Values may contain {name} placeholders, substituted at render via t(key, vars).
 */
export const en = {
  // Common
  'common.unknown': 'Unknown',
  'common.tryAgain': 'Try again',
  'common.walletFallback': 'Wallet',
  'common.loadingWallets': 'Loading your wallets...',
  'common.back': 'Back',
  'common.close': 'Close',
  'common.loading': 'Loading…',
  'common.select': 'Select',
  'common.selected': 'Selected',
  'common.beta': 'BETA',
  'common.cancel': 'Cancel',
  'common.refresh': 'Refresh',
  'common.refreshing': 'Refreshing',
  'common.all': 'All',
  'common.wallets': 'wallets',
  'common.wallet': 'wallet',
  'common.networks': 'networks',
  'common.network': 'network',
  'common.pending': 'Pending',
  'common.unavailable': 'Unavailable',
  'common.unverified': 'Unverified',
  'common.settings': 'Settings',
  'menu.title': 'Menu',
  'menu.open': 'Open menu',
  'menu.close': 'Close menu',
  'common.tier.testnet': 'Testnet',
  'common.tier.mainnet': 'Mainnet',
  'common.theme.auto': 'Auto',
  'common.theme.light': 'Light',
  'common.theme.dark': 'Dark',

  // Navigation
  'nav.primary': 'Primary',
  'nav.home': 'Home',

  // Settings — page
  'settings.title': 'Settings',

  // Settings — approvals section
  'settings.approvals.title': 'Approvals',
  'settings.approvals.hint':
    'Review transactions waiting on your decision before the gateway signs them.',
  'settings.approvals.link': 'Review pending approvals',

  // Settings — appearance section
  'settings.appearance.title': 'Appearance',
  'settings.appearance.hint': 'Choose how the app looks.',
  'settings.appearance.themeGroup': 'Theme',

  // Settings — language section
  // The pre-auth menu on the signed-out screens. /settings needs an account, so
  // language and appearance are surfaced here instead.
  // The app menu — the parent of the settings gear. Rows needing an account render
  // greyed with the locked hint until there is a session.
  'preauth.menu.title': 'Language & appearance',
  'preauth.menu.open': 'Open language and appearance menu',
  'settings.language.title': 'Language',
  'settings.language.hint': 'Choose your language.',

  // Settings — developer mode section
  'settings.devmode.title': 'Developer Mode',
  'settings.devmode.label': 'Developer Mode',
  'settings.devmode.desc': 'On uses testnet. Off uses mainnet.',
  'settings.devmode.note': 'Testnet mode. Balances have no real value.',

  // Settings — deployment section
  'settings.deployment.title': 'Deployment',
  'settings.deployment.network': 'Network',
  'settings.deployment.gatewayEnv': 'Gateway environment',
  'settings.deployment.bitcoinNetwork': 'Bitcoin network',
  'settings.deployment.organization': 'Organization',

  // Settings — security section (device passkeys)
  'settings.security.title': 'Security',
  'settings.security.hint':
    'Add a device passkey to approve transactions on this device before the gateway signs them.',
  // Sub-heading for the passkey list — the only block Security holds today.
  'settings.security.methodsTitle': 'Authentication methods',
  // "Sign-in method", not "device": enrolment is refused per authenticator, and one
  // device can hold several (iCloud Keychain, a browser password manager, a security
  // key). Saying "device" would tell someone their phone was used up when it was not.
  'settings.security.addDevice': 'Add sign-in method',
  // Replaces the add button once the account is full. Says what to do next,
  // because removing one is the only way forward and it does free a slot.
  'settings.security.limitReached': 'Maximum of {max} authentication methods reached.',
  'settings.security.adding': 'Waiting for device…',
  'settings.security.remove': 'Remove',
  'settings.security.removing': 'Removing…',
  'settings.security.empty': 'No device passkeys enrolled yet.',
  'settings.security.loading': 'Loading authentication methods…',
  'settings.security.retry': 'Try again',
  'settings.security.unsupported': 'This browser does not support device passkeys.',
  'settings.security.enrolledOn': 'Added {date}',
  'settings.security.lastUsed': 'Last used {date}',
  'settings.security.deviceFallbackLabel': 'Device passkey',

  // Create-a-passkey prompt (one-time, after a fresh OAuth or OTP login)
  'passkey.create.title': 'Create a passkey',
  'passkey.create.body':
    'Sign in with your fingerprint, face, or screen lock. No password to remember.',
  'passkey.create.cta': 'Create a passkey',
  'passkey.create.creating': 'Waiting for your device…',
  'passkey.create.dismiss': "Don't show this again",
  'passkey.create.error': 'Could not create a passkey. Try again, or add one later from Security in the menu.',
  // Thrown on the APPROVE step of a send when the wallet cannot establish which
  // sign-in methods the account has, after the request has already been retried.
  // It refuses rather than approving unstamped, so the first job of this string is
  // to say the transfer did not go through.
  'passkey.stateUnknown':
    'Your transfer was not sent. Could not confirm your sign-in methods — check your connection and try again.',

  // Settings — account section
  'settings.account.title': 'Account',
  'settings.account.signout': 'Sign out',

  // Login — page
  'login.title': 'Continue to your wallet',
  'login.eyebrow': 'Welcome',
  'login.error.start': 'Could not start sign-in. Check your connection and try again.',
  'login.divider': 'or',
  'login.more': 'More ways to sign in',

  // Login — providers ({name} is the brand for the fallback label)
  'login.provider.google': 'Continue with Google',
  'login.provider.apple': 'Continue with Apple',
  'login.provider.discord': 'Continue with Discord',
  'login.provider.twitter': 'Continue with X',
  'login.provider.kakao': 'Continue with Kakao',
  'login.provider.fallback': 'Continue with {name}',
  'login.provider.unavailable': 'This provider is not available right now.',

  // Login — passkey sign-in
  'login.passkey.button': 'Secure sign-in',
  'login.passkey.desc': 'Uses a verification method registered on your device, such as Face ID or fingerprint authentication',
  'login.passkey.pending': 'Waiting for your passkey…',
  // Browsers deliberately report "you dismissed the prompt" and "this device has no
  // passkey for this relying party" as the same NotAllowedError, so as not to leak
  // whether a credential exists. The copy must therefore cover both: naming only
  // cancellation sends someone whose device simply has no passkey here into an
  // endless retry. Reachable in normal use — a passkey enrolled on localhost during
  // development is, correctly, not usable on a deployed host.
  'login.passkey.cancelled':
    'Sign-in was cancelled, or this device has no passkey for this site. Try again or choose a provider.',
  'login.passkey.error': 'Could not sign in with a passkey. Try again or choose a provider.',
  // Shown when sign-in found no passkey or no account. Passkey creation lives in the
  // low-weight "create an account with a passkey" link below, so this points there.
  'login.passkey.noAccount': 'No passkey found on this device. Use "Create a wallet" below to make one.',

  // Login — passkey signup (a low-weight link below sign-in for a new user)
  'login.signup.prompt': 'New here?',
  'login.signup.link': 'Create a wallet',
  'login.signup.pending': 'Creating your account…',
  'login.signup.exists': 'You already have an account. Sign in with your passkey instead.',
  'login.signup.error': 'Could not create an account. Try again or choose a provider.',

  // Login — organization escape hatch (inside the "more ways to sign in" section)
  'login.org.label': 'Organization ID',
  'login.org.note': 'Leave blank to sign in to your own space. Then choose a provider above.',

  // Login — developer fallback
  'login.dev.body': 'For local development against a gateway, set a token from the browser console:',

  // Dashboard (home)
  'dashboard.offline': 'You appear to be offline. Reconnect to load your wallets.',
  'dashboard.empty': 'No wallets yet. Create one to get started.',

  // Home components
  'home.account.allWallets': 'All Wallets',
  'home.account.subtitle': '{wallets} {walletWord} · {networks} {networkWord}',
  'home.switcher.aria': 'Switch account, currently {name}',
  'home.copy.aria': 'Copy {name} address',
  'home.portfolio.aria': 'Portfolio balance',
  'home.portfolio.total': 'Total balance',
  'home.actions.aria': 'Wallet actions',
  'home.action.swapUnavailable': 'Swap is not available yet.',
  'home.action.buySellUnavailable': 'Buy and sell are not available yet.',
  'home.action.buySell': 'Buy/Sell',
  'home.network.testnetTitle': 'Testnet deployment. Balances shown here have no real value.',
  'home.network.mainnetTitle': 'Mainnet. Balances reflect the live network.',

  // App navigation / account-wide surfaces
  'activity.title': 'Activity',
  'activity.history': 'History',

  // Transaction list
  'tx.aria.history': 'Transaction history',
  'tx.date.pending': 'Pending',
  'tx.direction.receive': 'Receive',
  'tx.direction.send': 'Send',
  'tx.direction.swap': 'Swap',
  'tx.relation.from': 'From',
  'tx.relation.to': 'To',
  'tx.swapDetails.aria': 'Swap details: {from} to {to}',
  'tx.status.pending': 'Pending',
  'tx.status.pending_approval': 'Awaiting approval',
  'tx.status.pending_policy': 'Policy check',
  'tx.status.signing': 'Signing',
  'tx.status.signed': 'Signed',
  'tx.status.submitted': 'Submitted',
  'tx.status.broadcast': 'Broadcast',
  'tx.status.confirmed': 'Confirmed',
  'tx.status.failed': 'Failed',
  'tx.status.rejected': 'Rejected',
  'tx.status.unknown': 'Unknown',
  'tx.loading': 'Loading activity…',
  'tx.empty': 'No activity yet.',
  'tx.empty.hint': 'Your sends and swaps will show up here.',
  'tx.loadingMore': 'Loading more…',
  'tx.explorer.aria': 'View on the block explorer',

  // Approvals
  'approvals.title': 'Approvals',
  'approvals.error.load': 'Could not load approvals',
  'approvals.empty.title': 'You are all caught up',
  'approvals.empty.body':
    'No transactions are waiting on a decision. New requests that need your review will appear here.',
  'approvals.wallet.title': 'Approvals for this wallet',
  'approvals.wallet.resolving': 'Checking which approvals belong to this wallet…',
  'approvals.wallet.empty.title': 'Nothing waiting for this wallet',
  'approvals.wallet.empty.body':
    'No transactions on this wallet are waiting on a decision. Requests that need your review appear here.',
  'approvals.status.pending': 'Pending',
  'approvals.status.approved': 'Approved',
  'approvals.status.rejected': 'Rejected',
  'approvals.status.expired': 'Expired',
  'approvals.quorum': '{received} of {required} approvals',
  'approvals.quorum.aria': 'Quorum progress',
  'approvals.waiting.more': 'Waiting for {count} more {word}',
  'approvals.waiting.approval': 'approval',
  'approvals.waiting.approvals': 'approvals',
  'approvals.waiting.signing': 'Waiting for the signing ceremony to finish',
  'approvals.timeline.checker': 'A checker',
  'approvals.timeline.acted': 'acted',
  'approvals.detail.loading': 'Loading the exact transaction…',
  'approvals.detail.unavailable':
    'The transaction details could not be loaded. Approve or reject only if you can confirm this transfer another way.',
  'approvals.detail.notProvided': 'Not provided by the transaction record',
  'approvals.detail.requestedBy': 'Requested by',
  'approvals.detail.transactionStatus': 'Transaction status',
  'approvals.summary.transaction': 'Transaction',
  'approvals.action.busy':
    'Coordinating the multi-party signature. At quorum the gateway signs and broadcasts before responding, which can take a few seconds.',
  'approvals.action.approve': 'Approve',
  'approvals.action.reject': 'Reject',
  'approvals.confirm.approveTitle': 'Approve this transaction?',
  'approvals.confirm.rejectTitle': 'Reject this transaction?',
  'approvals.confirm.approveConsequence':
    'This relays your approval to the gateway. Once the quorum is met the gateway signs and broadcasts this transaction before responding, which can take a few seconds. Do not close the app while it completes.',
  'approvals.confirm.rejectConsequence':
    'This stops the transaction from being signed. The quorum can no longer be reached, so it will not be broadcast.',
  'approvals.confirm.reasonLabel': 'Reason (optional)',
  'approvals.confirm.reasonPlaceholder': 'Add a note for the audit trail',
  'approvals.confirm.approving': 'Approving',
  'approvals.confirm.rejecting': 'Rejecting',
  'approvals.section.pending': 'Needs your review ({count})',
  'approvals.section.decided': 'Decided',

  // Create wallet
  'create.title': 'Create wallet',
  'create.lead':
    'Choose a wallet type and a name. Key generation runs securely on the server; your device holds no key and signs nothing here.',
  'create.walletName': 'Wallet name',
  'create.walletPlaceholder': 'My wallet',
  'create.walletType': 'Wallet type',
  'create.kind.evm.label': 'EVM account',
  'create.kind.evm.desc': 'Ethereum · Base · BSC · HyperEVM',
  'create.kind.tron.label': 'Tron',
  'create.kind.tron.desc': 'TRX and TRC-20 tokens',
  'create.kind.solana.label': 'Solana',
  'create.kind.solana.desc': 'SOL and SPL tokens',
  'create.kind.sui.label': 'Sui',
  'create.kind.sui.desc': 'SUI and Sui tokens',
  'create.kind.bitcoin.label': 'Bitcoin',
  // BTC · SegWit, NOT Taproot. The provisioned Bitcoin wallet is SegWit v0
  // (`bitcoin`, secp256k1); `bitcoin-taproot` is a separate registry key on the
  // Schnorr curve and is withheld while its reshare gate is open. Staging's
  // format is kept; only the address type is corrected.
  'create.kind.bitcoin.desc': 'BTC · SegWit address',

  // Provisioning — the last step of registration, while the account's wallets
  // are created. The copy must never suggest the user is choosing an address:
  // the cycling values on screen are decoration, not candidates.
  // "Creating", not "Setting up". This screen watches wallets being CREATED —
  // real DKG ceremonies — and the masthead above it says "Create wallet", so
  // two words for one action read as two different things. Product decision,
  // 2026-08-19.
  'provisioning.title': 'Creating your wallets',
  'provisioning.unavailable': 'Not available here',
  // Shown with the action below it, on a screen that PARKS rather than
  // redirecting. Product decision, 2026-08-19.
  //
  // It used to read "We could not start setup. Your account is fine — you can
  // continue and try again from the wallet list." Two problems: it sent the user
  // to a wallet list that had no retry to offer, and "continue" implied the
  // account was usable when a new one has no wallets at all. This says only what
  // happened, and the button says what to do.
  'provisioning.failed': 'We couldn’t generate your wallets automatically.',
  // The escape to the manual form — the same one-chain-at-a-time flow that
  // existed before auto-provisioning. "manually" is load-bearing: it tells the
  // user this is a different route to the same outcome, not a retry of the thing
  // that just failed.
  'provisioning.createManually': 'Create wallet manually',
  // The three endings, shown when the fixed window closes. Each is a line saying
  // what happened plus one action; only the destination differs.
  //
  // SUCCEEDED means every row settled — the poll saw a usable wallet for each
  // chain in the plan.
  'provisioning.succeeded': 'Your wallets are ready.',
  // PARTIAL is the ORDINARY case, not an edge one: the window is a fixed three
  // seconds and does not wait for the ceremonies, so a slow one routinely
  // outlasts it. Saying "ready" here would be false, and the home screen's
  // "still creating" banner is what picks the rest up.
  'provisioning.partial':
    '{created} wallets were created successfully, but {missing} couldn’t be created.',
  'provisioning.partialHint': 'You can add the missing wallets manually later.',
  // Nothing has landed yet, but the ceremonies ARE running. Must not read as an
  // error and must not offer the manual form — that is how a user hand-builds a
  // second wallet on a chain the server is already finishing.
  'provisioning.pending': 'Your wallets are still being created.',
  'provisioning.pendingHint': 'This takes a moment — they’ll appear on your home screen.',
  // Shared by both non-failure endings: the wallets exist or are on their way, so
  // the action is the same and only the sentence above it changes.
  //
  // The KEY names the destination, the VALUE names the intent. "Continue" is what
  // the user is doing — finishing setup — where "Go to home" described the
  // mechanism and read like leaving something unfinished. Leaving the key as
  // `goHome` means a future wording change costs one value rather than a rename
  // across six catalogues.
  'provisioning.goHome': 'Continue',

  // A frozen wallet's raw lifecycle status is still "active" — freezing is a
  // separate timestamp — so the settings panel needs its own word for it, or it
  // tells the user their wallet is fine when it cannot sign.
  'walletSettings.statusFrozen': 'Frozen',

  // The home screen's empty state: a BUTTON ONLY, no message. Product decision,
  // 2026-08-18.
  //
  // It used to read "Your wallets are being created." on the reasoning that an
  // empty list is a TIMING state, since registration provisions automatically.
  // That asserts work is underway, which the screen cannot know — provisioning
  // may never have started — and it contradicts the button sitting under it. A
  // lone button says "you can get a wallet here" without claiming anything that
  // might be false.
  //
  // THE ONLY CALL TO ACTION FOR WALLET SETUP. The partial banner below used to
  // carry a second one — a "Check setup" link to this same route — which read as
  // two ways to do one thing. It was removed: the dashboard already re-runs
  // provisioning on mount, so that link was a redundant retry as well as a
  // duplicate label.
  'dashboard.createWallet': 'Create your wallet',
  // Shown on a PARTIALLY provisioned account, where the empty state no longer
  // applies because at least one wallet exists. Without it the user sees a
  // normal home screen quietly missing networks.
  'dashboard.stillCreating': 'Still creating {count} more wallet(s).',
  'create.busy': 'Running key generation ceremony',
  'create.toast.created': 'Wallet created.',

  // OAuth callback
  'oauth.pending.title': 'Signing you in',
  'oauth.pending.message': 'Completing sign-in, one moment.',
  'oauth.failure.title': 'Sign-in did not complete',
  'oauth.failure.fallback': 'Sign-in could not be completed.',
  'oauth.backToSignIn': 'Back to sign in',

  // Receive ({chain} is the resolved chain name)
  'receive.title': 'Receive',
  'receive.subtitle': 'Choose a chain and address to receive on',
  'receive.chain.fallback': 'This wallet',
  'receive.chainSelector.aria': 'Choose a chain to receive on',
  'receive.addressPicker.label': 'Address',
  'receive.addressPending': 'Address pending',
  'receive.qr.title': 'QR code for the {chain} address',
  'receive.qr.unavailable': 'QR code unavailable. Copy the address below.',
  'receive.addressLabel': '{chain} address',
  'receive.warning': 'Only send {chain} assets to this address.',
  'receive.copy.copy': 'Copy address',
  'receive.copy.copied': 'Address copied',
  'receive.copy.failed': 'Copy failed',
  'receive.copy.aria': 'Copy address to clipboard',
  'receive.pending.setup':
    'This wallet is still being set up. Its address will appear once it is ready.',
  'receive.pending.loading': 'Loading this wallet’s address...',
  'receive.empty': 'No wallets to receive into yet.',

  // Send ({asset}, {chain}, {label}, {reason} are interpolated at render)
  'send.title': 'Send',
  'send.selectAsset': 'Select asset to send',
  'send.loadingAssets': 'Loading your assets...',
  'send.emptyAssets': 'No assets with a spendable balance yet. Receive funds to send.',
  'send.emptyNoWallets': 'No wallets yet. Create one to send from.',
  'send.backToAssets': 'Back to assets',
  'send.stepTwo.title': 'Send {asset} on {chain} Network',
  'send.context.none': 'No wallet selected.',
  'send.context.from': 'From {label} on {chain}',
  'send.context.fromUnsupported': 'From {label}. {reason}',
  'send.error.wrongTier':
    'This wallet is not on the active network. Switch networks or pick a wallet on the current one before sending.',
  'send.error.noWallet': 'Select a wallet to send from.',
  'send.error.chainUnavailable': 'Sending on this chain is not available.',
  'send.status.submitting': 'Submitting the transaction.',
  'send.status.buildingPayload': 'Reading the network fee and nonce.',
  'send.status.pendingPolicy':
    'This transfer needs an extra approval or is time-locked by policy. It will not proceed until that is satisfied.',
  'send.status.noApproval':
    'The transaction was created but its approval is not available to act on here. Review it from the approvals surface to continue.',
  'send.confirm.title': 'Confirm send',
  'send.confirm.note':
    'Check the destination address carefully. This moves funds and cannot be undone.',
  'send.confirm.to': 'To',
  'send.confirm.amount': 'Amount',
  'send.confirm.network': 'Network',
  'send.confirm.networkFee': 'Network fee',
  'send.confirm.networkFeeValue':
    'Estimated and applied by the gateway when it builds the transaction.',
  'send.confirm.networkFeeValueEvm': 'Read from the network when you confirm, then applied to the transaction.',
  'send.confirm.submit': 'Confirm and send',
  'send.confirm.sending': 'Sending',
  'send.status.title': 'Send status',
  'send.status.statusLabel': 'Status',
  'send.status.txId': 'Transaction id',
  'send.status.hash': 'Hash',
  'send.status.explorer': 'View on block explorer',
  'send.status.goToApprovals': 'Go to approvals',
  'send.status.backToSend': 'Back to send',
  'send.result.status': 'Transfer {status}.',
  'send.result.statusReason': 'Transfer {status}{reason}',

  // Send form and asset picker
  'send.form.recipient': 'Recipient address',
  'send.form.destinationPlaceholder': 'Destination address',
  'send.form.noWallet': 'Select a wallet to send from.',
  'send.form.chainUnavailable': 'Sending on this chain is not available here.',
  'send.validation.recipientRequired': 'Enter a recipient address.',
  'send.validation.evmWebUnavailable':
    'Sending on this chain is not available in the web app.',
  'send.validation.evmNoNetwork': 'Choose which coin to send so the network can be resolved. This account holds one address across every EVM network.',
  'send.validation.evmTokenUnsupported': "Sending tokens on EVM networks is not available in the web app yet. Use the mobile app for token transfers, or send the network's own coin here.",
  'send.validation.evmImportedTokenUnsupported': 'Imported tokens cannot be sent from the web app. Only registry-verified tokens and the network\'s own coin can be sent here.',
  'send.validation.evmTokenDecimalsUnknown': 'This token\'s precision could not be determined, so the transfer cannot be encoded safely. Nothing was signed.',
  'send.validation.evmPrefix': 'An EVM address starts with 0x.',
  'send.validation.evmFormat': 'An EVM address is 0x followed by 40 hex characters.',
  'send.validation.evmChecksum': 'That EVM address has an invalid checksum.',
  'send.validation.evmZeroAddress': 'That is the zero address; funds sent there cannot be recovered.',
  'send.validation.suiPrefix': 'A Sui address starts with 0x.',
  'send.validation.suiFormat':
    'A Sui address is 0x followed by 64 lowercase hex characters.',
  'send.validation.tronPrefix': 'A Tron address starts with T.',
  'send.validation.tronLength': 'A Tron address is 34 characters.',
  'send.validation.tronChecksum': 'That Tron address has an invalid checksum.',
  'send.validation.tronInvalid': 'That is not a valid Tron address.',
  'send.validation.solanaInvalid': 'That does not look like a Solana address.',
  'send.validation.solanaBytes': 'A Solana address decodes to 32 bytes.',
  'send.validation.bitcoinChecksum': 'That Bitcoin address has an invalid checksum.',
  'send.validation.bitcoinInvalid': 'That is not a valid Bitcoin address.',
  'send.validation.bitcoinSegwitInvalid': 'That is not a valid SegWit address.',
  'send.validation.bitcoinTaprootInvalid': 'That is not a valid Taproot address.',
  'send.validation.bitcoinWrongNetwork':
    'That address is for a different Bitcoin network (expected {expected}).',
  'send.validation.bitcoinDust': 'Amount is below the Bitcoin dust limit of {sats} sats.',
  'send.form.amountRequired': 'Enter an amount.',
  'send.form.numberRequired': 'Enter a number.',
  'send.form.amountPositive': 'Amount must be greater than zero.',
  'send.form.decimals': '{symbol} supports at most {decimals} decimal places.',
  'send.form.exceedsBalance': 'Amount exceeds your {symbol} balance.',
  'send.form.assetAria': 'Asset',
  'send.form.noAssets': 'No assets',
  'send.form.available': 'Available: {amount} {symbol}',
  'send.form.review': 'Review send',
  'send.form.clear': 'Clear',
  'send.assets.searchLabel': 'Search assets',
  'send.assets.searchPlaceholder': 'Search by name or symbol',
  'send.assets.empty': 'No assets match. Clear the search or filter to see your holdings.',

  // Swap ({decimals}, {provider}, {count} are interpolated at render)
  'swap.title': 'Swap',
  'swap.subtitle': 'Best route across every provider',
  'swap.balance': 'Balance {amount} {symbol}',
  'swap.max': 'Max',
  'swap.signer.overridden': '{selected} can’t swap, so {label} · {address} is being used.',
  'swap.empty.evm':
    'Swapping needs a wallet with an EVM address. Create one from the home screen first.',
  'swap.error.loadChains': 'failed to load chains',
  'swap.error.quote': 'quoting failed',
  'swap.error.executeNotEnabled':
    'Swap execution is not enabled on this service (development signer off).',
  'swap.error.executeFallback': 'swap failed',
  'swap.side.from': 'You pay',
  'swap.side.to': 'You receive',
  'swap.chain.fromAria': 'From chain',
  'swap.chain.toAria': 'To chain',
  'swap.amount.fromAria': 'Amount to send',
  'swap.amount.toAria': 'Amount to receive',
  'swap.amount.invalidHint': 'Enter a positive amount with at most {decimals} decimals.',
  'swap.flip.aria': 'Swap direction',
  'swap.sameTokenHint': 'Pick two different tokens to swap.',
  'swap.warning.insufficientBalance':
    'Your balance is lower than this amount, so the swap would fail.',
  'swap.warning.insufficientAllowance': 'A token approval will be required before this swap.',
  'swap.toast.submitted': 'Swap submitted via {provider}. Track it in Activity.',
  'swap.confirm.swapping': 'Swapping…',
  'swap.confirm.via': 'Swap via {provider}',
  'swap.confirm.summary': 'Swapping {inAmount} {inSymbol} for about {outAmount} {outSymbol} via {provider}. Settlement completes asynchronously on the destination network.',
  'swap.mpcNote': 'Signed by your wallet through the MPC signing flow; the origin transaction broadcasts on-chain and the provider settles the fill.',
  'swap.confirm.default': 'Swap',
  'swap.confirm.disabledTitle': 'Swap execution ships with the gateway signing integration',
  'swap.mock.enabledHint': 'Signed by the development wallet (mock MPC) · real on-chain swap',
  'swap.mock.disabledHint': 'Quotes are live · executing swaps arrives with the gateway signing flow',
  'swap.picker.tokenAria': 'Choose a token',
  'swap.picker.chainAria': 'Choose a chain',
  'swap.picker.searchToken': 'Search name, symbol, or address',
  'swap.picker.searchChain': 'Search wallets',
  'swap.picker.loadingTokens': 'Loading tokens…',
  'swap.picker.noTokens': 'No tokens match.',
  'swap.picker.noChains': 'No chains match.',
  'swap.picker.noWallets': 'No wallet can receive on any routable chain.',
  'swap.picker.routes': '{count} routes',
  'swap.token.aria': 'Choose token',
  'swap.quote.noRoute': 'No route',
  'swap.quote.best': 'Best',
  'swap.quote.networkCost': 'Network cost',
  'swap.quote.platformFee': 'Platform fee',
  'swap.quote.priceImpact': 'Price impact',
  'swap.quote.time': 'Time',

  // Swap detail sheet
  'swap.detail.aria': 'Swap details',
  'swap.detail.closeAria': 'Close details',
  'swap.detail.status': 'Status',
  'swap.detail.paid': 'You paid',
  'swap.detail.received': 'You received',
  'swap.detail.rate': 'Rate',
  'swap.detail.route': 'Route',
  'swap.detail.provider': 'Provider',
  'swap.detail.feeQuoted': 'Fee (quoted)',
  'swap.detail.feeCharged': 'Fee (charged)',
  'swap.detail.sourceTx': 'Source tx',
  'swap.detail.destinationTx': 'Destination tx',

  // Swap origin axis — what the CHAIN says about the transaction we signed.
  // Separate from the provider status above, and deliberately weaker than it
  // could be: the failure these exist for was a swap that succeeded while the
  // app knew nothing, so overclaiming is the expensive mistake.
  'swap.origin.unreported': 'Submitting — the outcome is not known yet.',
  'swap.origin.reported': 'Checking the network for your transaction.',
  'swap.origin.verified': 'Sent on the origin chain — waiting for the destination.',
  'swap.origin.reverted': 'The transaction ran and failed. No funds moved.',
  'swap.origin.unknown':
    'We could not confirm this transaction. Check your wallet history before trying again.',
  // Shown in place of a retry button wherever a broadcast may still be
  // outstanding: a fresh attempt there could send a second transaction.
  'swap.origin.noRetry': 'Retry is unavailable until this transaction is confirmed or ruled out.',
  'swap.origin.ackPrompt':
    'This swap is unconfirmed — releasing it requires checking your wallet history first.',
  'swap.origin.ackRelease': 'I checked my wallet history — release this hold',
  'swap.origin.trackingFailed':
    'This swap could not be recorded, so it was not started. Nothing was signed and no funds moved.',

  // Earn (lending market; {symbol}, {amount}, {usd}, {digest}, {reason} interpolated)
  'earn.title': 'Lend & Borrow',
  'earn.subtitle':
    'Increase your investment potential by using your existing assets as collateral to borrow funds, or lend them to earn yield.',
  'earn.error.market': 'Market unavailable: {message}',
  'earn.error.retry': 'Try again',
  'earn.loading': 'Loading lending markets…',
  'earn.empty': 'No lending markets are available right now.',
  'earn.table.asset': 'Asset',
  'earn.table.totalSupply': 'Total supply',
  'earn.table.totalBorrow': 'Total borrow',
  'earn.table.ltv': 'LTV',
  'earn.table.aria': 'Lending markets',
  'earn.vault.title': '{symbol} Vault',
  'earn.vault.supplyYield': 'Supply yield',
  'earn.vault.borrowInterest': 'Borrow interest',
  'earn.vault.marketPrice': 'Market price',
  'earn.vault.utilization': 'Utilization',
  'earn.vault.depositThreshold': 'Deposit threshold',
  'earn.vault.borrowThreshold': 'Borrow threshold',
  'earn.vault.borrowFee': 'Borrow fee: {pct}%',
  'earn.account.title': 'Your account',
  'earn.account.deposits': 'Your deposits',
  'earn.account.borrows': 'Your borrows',
  'earn.account.borrowLimit': 'Borrow limit',
  'earn.account.liqThreshold': 'Liq. threshold',
  'earn.account.health': 'Account health',
  'earn.account.depositedRow': 'Deposited {amount} {symbol} (${usd})',
  'earn.account.borrowedRow': 'Borrowed {amount} {symbol} (${usd})',
  'earn.action.supply': 'Deposit',
  'earn.action.borrow': 'Borrow',
  'earn.action.withdraw': 'Withdraw',
  'earn.action.repay': 'Repay',
  'earn.actions.aria': 'Lending action',
  'earn.amount.label': 'Amount ({symbol})',
  'earn.amount.available': 'available ~{amount}',
  'earn.amount.max': 'Max',
  'earn.amount.allDebt': 'All debt',
  'earn.submit.submitting': 'Submitting…',
  'earn.note.sameAssetBorrow':
    "You can't borrow {symbol} — it's your collateral. Pick a different asset to borrow, or withdraw the deposit first.",
  'earn.note.overMax.withdraw':
    'Only ~{amount} {symbol} can be withdrawn — the rest must stay as collateral for your outstanding borrow. Repay first to withdraw everything.',
  'earn.note.overMax.borrow': 'Your collateral supports borrowing up to ~{amount} {symbol}.',
  'earn.note.overMax.repay': 'You only owe ~{amount} {symbol}.',
  'earn.note.noSigner':
    'Execution is unavailable: signing happens through the gateway, and neither the gateway lending route nor the local dev signer is reachable.',
  'earn.note.devSigner': 'Dev signer (testnet): {address}',
  'earn.toast.confirmed': '{action} confirmed ({digest}…)',
  'earn.toast.failedReason': '{action} failed: {reason}',
  'earn.toast.failedOnChain': '{action} failed: on-chain error ({digest}…)',
  'earn.activity.title': 'Recent activity',

  // Filters and lists
  'filter.network.aria': 'Network filter',
  'filter.chain.aria': 'Blockchain filter',
  'filter.chain.selectNetwork': 'Select network',
  'holdings.empty': 'No holdings here yet. Receive assets or switch the filter to see more.',
  'holdings.showHidden': 'Show {count} hidden · under $0.01',
  'holdings.hideSmall': 'Hide {count} small {word}',
  'holdings.balance': 'balance',
  'holdings.balances': 'balances',
  'chainSummary.empty': 'No holdings here yet. Receive assets to see your chains.',
  'chainSummary.viewAria': 'View {name}, {count} {word}',
  'walletChain.empty': 'No wallets on this chain yet.',
  'walletChain.openAria': 'Open {label}',

  // Account and wallet management sheets
  'account.closeSwitcher': 'Close wallet switcher',
  'account.title': 'Your wallets',
  'account.allDesc': 'everything, one list',
  'account.newWallet': 'New wallet',
  'account.searchPlaceholder': 'Search by name or address',
  'account.searchAria': 'Search wallets by name or address',
  'account.networks': 'Networks',
  'account.empty.filtered': 'No wallets match your filters.',
  'account.empty.none': 'No wallets yet.',
  'account.selectAria': 'Select {label}, {value}',
  'account.copyAddressAria': 'Copy {chain} address',
  'account.showQrAria': 'Show {chain} receive QR',
  'selectNetwork.closeAria': 'Close network picker',
  'selectNetwork.title': 'Select network',
  'selectNetwork.searchPlaceholder': 'Search networks',
  'selectNetwork.searchAria': 'Search networks by name',
  'selectNetwork.groupAria': 'Network',
  'selectNetwork.all': 'All networks',
  'selectNetwork.empty': 'No networks match your search.',
  'walletTabs.sectionsAria': 'Wallet sections',
  'walletTabs.tokens': 'Tokens',
  'walletTabs.approvals': 'Approvals',
  'walletTabs.manage': 'Manage crypto',
  'walletTabs.history': 'History',
  'manage.closeAria': 'Close manage crypto',
  'manage.aria': 'Manage crypto for {wallet}',
  'manage.title': 'Manage crypto',
  'manage.searchPlaceholder': 'Search tokens',
  'manage.searchAria': 'Search tokens by symbol or name',
  'manage.empty': 'This wallet holds no tokens yet. Receive assets to manage them here.',
  'manage.noMatches': 'No tokens match your search.',
  'manage.tokensAria': 'Tokens',
  'manage.showTokenAria': 'Show {symbol} on {network}',
  'manage.import': 'Import a custom token',
  'manage.note':
    'Only tokens this wallet holds are listed. Import a token by its contract address to track a custom asset. An imported token is not verified and carries no price.',
  'import.closeAria': 'Close import token',
  'import.aria': 'Import a token into {wallet}',
  'import.title': 'Import token',
  'import.contract': 'Contract address',
  'import.contractPlaceholder': 'Paste the token contract address',
  'import.contractAria': 'Token contract address',
  'import.resolve': 'Resolve token',
  'import.resolving': 'Resolving…',
  'import.unknownToken': 'Unknown token',
  'import.unnamedToken': 'Unnamed token',
  'import.decimals': 'Decimals',
  'import.balance': 'Balance',
  'import.hint':
    'Paste a token contract address on {network} and resolve it to preview the token before adding it.',
  'import.warning':
    'Unverified token. Verify the contract address yourself before adding it. An imported token is not on the curated allow-list and carries no price.',
  'import.add': 'Add token',
  'import.error.cap':
    'This wallet has reached the maximum number of imported tokens. Remove one to add another.',

  // Wallet balance and wallet settings
  'walletBalance.tag.unverifiedTitle': 'This token is not on the curated allow-list.',
  'walletBalance.testnetPillTitle': 'Testnet wallet. Balances have no real value.',
  'walletBalance.testnet': 'Testnet',
  'walletBalance.loadingBalances': 'Loading balances...',
  'walletBalance.statusPrefix': 'Status: {status}',
  'walletBalance.keygen': 'Key generation in progress. Balances appear once ready.',
  'walletBalance.partial': 'Some balances could not be read and may be incomplete.',
  'walletBalance.chainFilterAria': 'Chain filter',
  'walletBalance.testnetBanner':
    'Testnet - balances shown here are testnet and have no real value. Switch off Developer Mode in the menu to use mainnet.',
  'walletSettings.addressAria': 'Wallet address',
  'walletSettings.detailsAria': 'Wallet details',
  'walletSettings.address': 'Address',
  'walletSettings.details': 'Details',
  'walletSettings.addressPending':
    'Address pending. It appears once the wallet’s key finalizes.',
  'walletSettings.name': 'Name',
  'walletSettings.balance': 'Balance',
  'walletSettings.status': 'Status',
  'walletSettings.curve': 'Curve',
  'walletSettings.publicKey': 'Public key',
  'walletSettings.shortAddress': 'Short address',
  'walletSettings.pending': 'Pending',
  'walletSettings.unknownChain': 'Unknown chain',

  // Welcome / language choice (first-visit screen)
  'welcome.eyebrow': 'Welcome to',
  'welcome.tagline': 'Protecting what matters',
  'welcome.title': 'Choose your language',
  'welcome.cta': 'Get Started',
  'welcome.legalTitle': 'Legal Notice',
  'welcome.legalText': 'Language support is provided as part of our efforts to enhance accessibility for users worldwide. It is only available in regions where such use is legally permitted and is intended solely for the convenience of users who prefer or require it.',
  'welcome.legalAgreement': 'By clicking the "I Agree" button below, you confirm that your use of this language complies with local laws and regulations.',
  'welcome.agree': 'I Agree',

  // Start / create-wallet (the screen the welcome choice leads into). The brand
  // wordmark "PlantMe" is a proper noun and is not translated; this label under it
  // is. Errors and the pending state reuse the existing login.signup.* strings, so
  // one passkey failure reads the same wherever it is triggered.
  'start.label': 'Wallet',
  'start.points.badge': 'Powered by MPC technology',
  'start.points.lead': 'No exposed private keys or seed phrases to worry about.',
  'start.points.shares':
    'Your wallet is protected by security factors (key shards) distributed across Google, your backup, and device authentication.',
  'start.points.twoRequired': 'Moving assets requires at least two security factors.',
  'start.points.neverWhole':
    'The security factors protecting your assets are never combined or stored in one place.',
  'start.points.rules':
    'Additional security rules protect your assets even if something happens to a security factor.',
  'start.points.more': 'Read more',
  'start.points.less': 'Show less',
  'start.title': 'Create anonymous wallet',
  'start.desc': 'Protect your decentralized wallet using your device’s authentication.',
  'start.hint': 'Face ID · Fingerprint · Passkey',
  'start.signin.prompt': 'Already have an account?',
  'start.signin.link': 'Sign in',
  // Shown instead of the passkey panel where the browser has no WebAuthn API, so the
  // screen still offers a way in rather than a dead button.
  'start.unsupported': 'This browser cannot create a passkey. Sign in with a provider instead.',

  // Maintenance. Shown when the host answers /v1 with 503 (the Caddy gate).
  // Calm and factual, not an error: nothing is broken. The assurance line is
  // separate because during an outage it is the sentence people look for.
  'maintenance.title': 'The wallet is paused',
  'maintenance.body':
    'We are carrying out planned maintenance. The wallet will be back as soon as it is complete.',
  'maintenance.assurance': 'Your funds and keys are safe. Signing out does not change anything you hold.',
  'maintenance.retry': 'Check again',
  // Shown when someone taps "create a wallet" on a device that already has an
  // account. A courtesy, not a guard: signup mints a fresh user handle, so a second
  // account here is genuinely valid — it is just usually not what was meant, and
  // signing in is the likelier intent.
  //
  // Product-owner copy, kept verbatim. Note it is deliberately stronger than what the
  // check can prove: the flag records that an account was USED in this browser, not
  // that an authenticator is still registered on the device. Do not "correct" it to
  // match the mechanism without asking — the wording is a product decision.
  'start.secondAccount.title': 'This device already has an authenticator linked to an account',
  'start.secondAccount.body': 'Would you like to add another account?',
  // Says "go to" because that is what it does: it routes to the sign-in screen rather
  // than starting a ceremony. The flag is set by OAuth logins too, so this device may
  // hold no passkey at all, and firing one here would dead-end a Google user.
  'start.secondAccount.signIn': 'Go to sign in',
  'start.secondAccount.create': 'Add another account',
} satisfies Record<string, string>;

/** Every valid message key. Used to type the translate function and ko.ts. */
export type MessageKey = keyof typeof en;
